/**
 * CiaoExpertSelector — Compact selector button that opens a dropdown panel
 * Loads experts dynamically from the backend registry
 */

import React, { useState, useRef, useEffect } from 'react';
import { IoChevronDown, IoCheckmark } from 'react-icons/io5';
import { TbRobot } from 'react-icons/tb';
import type { ExpertInfo } from '../modules/ciaoApi';

interface CiaoExpertSelectorProps {
  experts: ExpertInfo[];
  selectedExpertId: string;
  onSelect: (expertId: string) => void;
  disabled?: boolean;
}

const CiaoExpertSelector: React.FC<CiaoExpertSelectorProps> = ({
  experts,
  selectedExpertId,
  onSelect,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  if (experts.length === 0) return null;

  const selected = experts.find(e => e.id === selectedExpertId) || experts[0];

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
      {/* Robot icon */}
      <TbRobot size={16} style={{ color: '#FC7556', flexShrink: 0, opacity: 0.8 }} />

      {/* Trigger button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 14px',
          borderRadius: 12,
          fontSize: 11.5,
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.15s ease',
          background: isOpen ? 'rgba(252, 117, 86, 0.12)' : 'rgba(128,128,128,0.1)',
          color: selectedExpertId !== 'auto' ? '#FC7556' : 'var(--text-color, #555)',
          border: selectedExpertId !== 'auto' ? '1px solid rgba(252, 117, 86, 0.4)' : '1px solid rgba(128,128,128,0.25)',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <span style={{ fontSize: 13, lineHeight: 1 }}>{selected.icon}</span>
        <span>{selected.displayName}</span>
        <IoChevronDown
          size={11}
          style={{
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            opacity: 0.5,
          }}
        />
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            marginBottom: 6,
            width: 280,
            maxHeight: 360,
            overflowY: 'auto',
            background: 'var(--background-color, #fff)',
            borderRadius: 12,
            boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid rgba(128,128,128,0.2)',
            zIndex: 50,
            animation: 'ciao-fade-in 0.12s ease-out',
            scrollbarWidth: 'thin',
          }}
        >
          <div style={{ padding: '8px 10px 4px 12px' }}>
            <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-color, #666)', opacity: 0.5 }}>
              Seleccionar experto
            </span>
          </div>
          {experts.map((expert) => {
            const isSelected = expert.id === selectedExpertId;
            return (
              <button
                key={expert.id}
                onClick={() => { onSelect(expert.id); setIsOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  width: '100%',
                  padding: '10px 12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                  background: isSelected ? 'rgba(252, 117, 86, 0.06)' : 'transparent',
                  borderLeft: isSelected ? '3px solid #FC7556' : '3px solid transparent',
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'rgba(128,128,128,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? 'rgba(252, 117, 86, 0.06)' : 'transparent'; }}
              >
                <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>{expert.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      fontSize: 12, fontWeight: 600,
                      color: isSelected ? '#FC7556' : 'var(--text-color, #333)',
                    }}>
                      {expert.displayName}
                    </span>
                    {isSelected && <IoCheckmark size={13} color="#FC7556" />}
                  </div>
                  <div style={{
                    fontSize: 10.5, color: 'var(--text-color, #666)', opacity: 0.6, lineHeight: 1.35, marginTop: 2,
                  }}>
                    {expert.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CiaoExpertSelector;
